import { useState } from "react";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import type {
  AgentFeishuPublishBinding,
  AgentFeishuPublishView,
} from "@dosia/shared";
import { apiCall } from "../../lib/api-client.js";
import { ApiError } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import {
  reviewsPublishedFeishuDisablePath,
  reviewsPublishedFeishuEnablePath,
  reviewsPublishedFeishuPath,
} from "../../lib/api-paths.js";
import { formatDateTime } from "../../lib/format.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { FormField, PrimaryButton, SecondaryButton, TextInput } from "../../components/FormField.js";

/**
 * 已发布 Agent「发布到飞书」区块（N-83 P3-01 任务组4，proposal 增量 14、15，界面样子 D17；
 * 返工：运行中 / 已停用两态也要带 App ID / Secret 表单——否则存量密文解不开
 * （400 feishu-app-secret-required）时管理员无处重填、也换不了飞书应用，参见
 * proposal 增量 14「App Secret（已配置时提示「已配置；留空=不修改」）」，这句提示只有
 * 已有绑定时才成立）。
 *
 * 四态共用同一份 App ID / Secret 表单 + 能力检查 + 主按钮（无绑定→「发布到飞书」，有绑定→
 * 「重新发布」，Secret 留空 = 沿用原密文）；active 额外带「停用」，disabled 额外带「重新启用」
 * （POST 无 body，走原密文重校验）。「已撤下」只显示撤下说明 + 表单 +「重新发布」、不给「重新启用」：
 * 本区块只挂在仍在已发布列表里的 Agent 上，能看到它说明 Agent 已被重新发布进池子，
 * 重新发布（完整走能力与凭证校验）是唯一的恢复入口。
 */

// ── 运行时守卫：服务端响应不信任前端类型标注，渲染前先校验关键字段（红线 #9：不用 `as` 冒充）。──

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isBindingStatus(v: unknown): v is AgentFeishuPublishBinding["status"] {
  return v === "active" || v === "disabled" || v === "withdrawn";
}

function isBinding(v: unknown): v is AgentFeishuPublishBinding {
  if (!isRecord(v)) return false;
  return (
    typeof v.appId === "string" &&
    typeof v.hasSecret === "boolean" &&
    isBindingStatus(v.status) &&
    typeof v.version === "number" &&
    typeof v.publishedAt === "string" &&
    typeof v.publishedBy === "string"
  );
}

function isView(v: unknown): v is AgentFeishuPublishView {
  if (!isRecord(v)) return false;
  if (typeof v.slug !== "string" || typeof v.agentName !== "string") return false;
  if (v.binding !== null && !isBinding(v.binding)) return false;
  if (typeof v.changedSinceSnapshot !== "boolean") return false;
  const r = v.readiness;
  if (!isRecord(r) || typeof r.publishable !== "boolean" || !Array.isArray(r.missing)) return false;
  return r.missing.every(
    (m) => isRecord(m) && typeof m.axis === "string" && typeof m.name === "string",
  );
}

function isBindingWrapper(v: unknown): v is { binding: AgentFeishuPublishBinding } {
  return isRecord(v) && isBinding(v.binding);
}

// ── 缺失能力 → 业务名（已知名映射表，proposal 增量 15：至少 chrome-devtools → 「浏览器」）。──

const KNOWN_MISSING_BUSINESS_KEYS: Record<string, string> = {
  "chrome-devtools": "admin.review.feishuPublish.knownCapability.chrome-devtools",
};

function missingDisplayName(t: (key: string, opts?: Record<string, unknown>) => string, name: string): string {
  const key = KNOWN_MISSING_BUSINESS_KEYS[name];
  if (!key) return name;
  return t("admin.review.feishuPublish.missingKnownFormat", { business: t(key), name });
}

// ── 错误码 → 文案（409 看 by/name；422 看 missing/reason；其它显示原始 error）。──

function errorMessage(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : String(err);
  const details = isRecord(err.details) ? err.details : undefined;
  switch (err.code) {
    case "feishu-app-id-in-use": {
      const by = details?.by;
      const name = typeof details?.name === "string" ? details.name : "";
      if (by === "role") return t("admin.review.feishuPublish.error.inUseRole", { name });
      if (by === "agent") return t("admin.review.feishuPublish.error.inUseAgent", { name });
      return t("admin.review.feishuPublish.error.inUseOrgMainApp");
    }
    case "feishu-publish-capability-missing": {
      const missing = Array.isArray(details?.missing) ? details.missing : [];
      const names = missing
        .filter((m): m is { axis: string; name: string } => isRecord(m) && typeof m.name === "string")
        .map((m) => missingDisplayName(t, m.name));
      return names
        .map((n) => t("admin.review.feishuPublish.missingLine", { name: n }))
        .concat(t("admin.review.feishuPublish.missingConclusion"))
        .join("\n");
    }
    case "feishu-credential-invalid": {
      const reason = typeof details?.reason === "string" ? details.reason : "";
      return t("admin.review.feishuPublish.error.credentialInvalid", { reason });
    }
    case "feishu-app-secret-required":
      return t("admin.review.feishuPublish.error.secretRequiredAgain");
    case "agent-feishu-binding-not-found":
      return t("admin.review.feishuPublish.error.bindingNotFound");
    case "review-channel-bad-response":
    case "review-channel-unreachable":
    case "review-channel-disabled":
      return t("admin.review.feishuPublish.error.channelUnavailable");
    case "invalid-app-id":
      return t("admin.review.feishuPublish.error.appIdRequired");
    default:
      // invalid-app-secret / invalid-slug / agent-not-published / 未列举的码 —— 原样显示错误码。
      return err.code;
  }
}

export function FeishuPublishSection({
  orgCode,
  slug,
}: {
  orgCode: string;
  slug: string;
}): JSX.Element {
  const { t } = useTranslation();
  const auth = useAtomValue(authAtom);
  const path = reviewsPublishedFeishuPath(orgCode, slug);
  const viewQ = useApiQuery<unknown>(path, [orgCode, slug]);

  // 管理员没动过 App ID 输入框时为 null，显示绑定里的 App ID；动过就以草稿为准，refetch 不会冲掉。
  const [appIdDraft, setAppIdDraft] = useState<string | null>(null);
  const [appSecret, setAppSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 停用只回 { binding }（企业后端挂了也能停）；GET 刷不回来时兜底渲染这份。
  const [bindingOverride, setBindingOverride] = useState<AgentFeishuPublishBinding | null>(null);

  const view = viewQ.data !== null && isView(viewQ.data) ? viewQ.data : null;
  const malformed = viewQ.data !== null && view === null;
  const effectiveBinding = view ? view.binding : viewQ.error && bindingOverride ? bindingOverride : null;

  const appId = appIdDraft ?? effectiveBinding?.appId ?? "";

  async function runAction(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  // 发布 / 重新发布共用一条：无绑定时 Secret 必填（按钮态已挡）；有绑定时 Secret 留空 =
  // 沿用原密文，body 就不带这个字段（红线：不能把空字符串当「要改成空」发过去）。
  const publish = (): Promise<void> =>
    runAction(async () => {
      const trimmedSecret = appSecret.trim();
      await apiCall("PUT", path, {
        token: auth?.token,
        body: {
          appId: appId.trim(),
          ...(trimmedSecret ? { appSecret: trimmedSecret } : {}),
        },
      });
      setAppSecret("");
      await viewQ.refetch();
    });

  const disable = (): Promise<void> =>
    runAction(async () => {
      const raw = await apiCall<unknown>("POST", reviewsPublishedFeishuDisablePath(orgCode, slug), {
        token: auth?.token,
      });
      if (!isBindingWrapper(raw)) throw new Error("视图格式异常");
      setBindingOverride(raw.binding);
      await viewQ.refetch();
    });

  const enable = (): Promise<void> =>
    runAction(async () => {
      await apiCall("POST", reviewsPublishedFeishuEnablePath(orgCode, slug), { token: auth?.token });
      await viewQ.refetch();
    });

  const publishDisabled =
    busy ||
    !view ||
    !view.readiness.publishable ||
    appId.trim() === "" ||
    (effectiveBinding === null && appSecret.trim() === "");

  const secretHint = effectiveBinding?.hasSecret
    ? t("admin.review.feishuPublish.appSecretHintConfigured")
    : t("admin.review.feishuPublish.appSecretHintFirst");

  return (
    <section className="space-y-2 border-t border-dosia-border pt-3">
      <div>
        <h4 className="text-sm font-semibold text-dosia-text">
          {t("admin.review.feishuPublish.title")}
        </h4>
        <p className="text-xs text-dosia-text-secondary">
          {t("admin.review.feishuPublish.description")}
        </p>
        <p
          data-testid="feishu-publish-group-duty-hint"
          className="mt-2 rounded-lg border border-dosia-warning/50 bg-dosia-warning/10 px-3 py-2 text-xs text-dosia-warning"
        >
          {t("admin.review.feishuPublish.groupDutyHint")}
        </p>
      </div>

      {viewQ.loading && !view ? (
        <p className="text-xs text-dosia-text-dim">{t("admin.review.feishuPublish.loading")}</p>
      ) : malformed ? (
        <ErrorBanner>{t("admin.review.feishuPublish.error.malformed")}</ErrorBanner>
      ) : viewQ.error && !effectiveBinding ? (
        <ErrorBanner>{errorMessage(viewQ.error, t)}</ErrorBanner>
      ) : (
        // ── 四态共用同一份表单 + 能力检查 + 主按钮 ──
        <div className="space-y-2">
          {effectiveBinding?.status === "withdrawn" && (
            <p className="text-sm text-dosia-text-secondary">
              {t("admin.review.feishuPublish.withdrawnStatus")}
            </p>
          )}
          {effectiveBinding?.status === "active" && (
            <>
              <p className="text-sm text-dosia-text">
                {t("admin.review.feishuPublish.activeStatus", {
                  version: effectiveBinding.version,
                  time: formatDateTime(effectiveBinding.publishedAt),
                  who: effectiveBinding.publishedBy,
                })}
              </p>
              {view?.changedSinceSnapshot && (
                <p className="text-xs text-amber-700">
                  {t("admin.review.feishuPublish.changedNotice", { version: effectiveBinding.version })}
                </p>
              )}
            </>
          )}
          {effectiveBinding?.status === "disabled" && (
            <p className="text-sm text-dosia-text">
              {t("admin.review.feishuPublish.disabledStatus", { version: effectiveBinding.version })}
            </p>
          )}

          <FormField label={t("admin.review.feishuPublish.appIdLabel")}>
            <TextInput
              value={appId}
              onChange={(e) => setAppIdDraft(e.target.value)}
              placeholder={t("admin.review.feishuPublish.appIdPlaceholder")}
            />
          </FormField>
          <FormField label={t("admin.review.feishuPublish.appSecretLabel")} hint={secretHint}>
            <TextInput
              type="password"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
            />
          </FormField>

          <div className="space-y-1">
            <div className="text-xs text-dosia-text-secondary">
              {t("admin.review.feishuPublish.readinessTitle")}
            </div>
            {view && view.readiness.publishable ? (
              <p className="text-sm text-dosia-text-secondary">
                {t("admin.review.feishuPublish.readinessOk")}
              </p>
            ) : view ? (
              <div className="space-y-0.5">
                {view.readiness.missing.map((m) => (
                  <p key={`${m.axis}-${m.name}`} className="text-xs text-amber-700">
                    {t("admin.review.feishuPublish.missingLine", { name: missingDisplayName(t, m.name) })}
                  </p>
                ))}
                <p className="text-xs text-amber-700">
                  {t("admin.review.feishuPublish.missingConclusion")}
                </p>
              </div>
            ) : null}
          </div>

          {error && <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>}

          <div className="flex justify-end gap-2">
            {effectiveBinding?.status === "active" && (
              <SecondaryButton danger disabled={busy} onClick={() => void disable()}>
                {t("admin.review.feishuPublish.disableButton")}
              </SecondaryButton>
            )}
            {effectiveBinding?.status === "disabled" && (
              <SecondaryButton disabled={busy} onClick={() => void enable()}>
                {t("admin.review.feishuPublish.enableButton")}
              </SecondaryButton>
            )}
            <PrimaryButton disabled={publishDisabled} onClick={() => void publish()}>
              {effectiveBinding === null
                ? t("admin.review.feishuPublish.publishButton")
                : t("admin.review.feishuPublish.republishButton")}
            </PrimaryButton>
          </div>
        </div>
      )}
    </section>
  );
}
